// Copyright 2024 Sorama B.V.

import { Point2 } from "libraries/DataTypes/Point2";
import { Unit } from "units/Unit";
import { LayoutBuilder } from "./LayoutBuilder";
import { SVGElementBuilder } from "./SVGElementBuilder";
import { SpiderChartSVGManager } from "../Managers/SpiderChartSVGManager";
import { PsychoAcousticMetricType, PsychoAcousticUnits } from "../SpiderChart";

/**
 * Type representing a mapping of each `PsychoAcousticMetricType` to its corresponding layout properties
 * (`PsychoAcousticMetricLayoutProperties`). Each metric type will have associated display properties.
 */
export type PsychoAcousticMetricsLayoutMap = {
    [key in PsychoAcousticMetricType]: PsychoAcousticMetricLayoutProperties;
};

/**
 * Interface defining the layout properties for each psychoacoustic metric.
 * - `displayName`: The name of the metric as displayed on the chart.
 * - `maxValuePoint`: The position (coordinates) of the outer polygon point where the metric reaches its maximum.
 * - `textPoint`: The position where the metric label (text) will be displayed.
 * - `unit`: The unit of the metric (e.g., "Nx" for Loudness).
 */
export interface PsychoAcousticMetricLayoutProperties {
    displayName: string;
    maxValuePoint: Point2; // The point of the outer polygon where the metric is at its maximum
    textPoint: Point2; // The point where the metric text is displayed
    unit: Unit; // The unit of the metric
}

/**
 * Factory class for creating and managing spider charts displaying psychoacoustic metrics.
 * It provides methods to generate SVG elements, adjust zoom behavior, and dynamically create chart layouts.
 */
export class SpiderChartFactory {
    // The opacities of the measurement polygons
    public static readonly POLYGON_OPACITY = "0.2";
    public static readonly POLYGON_HIGHLIGHT_OPACITY = "0.5";

    public static readonly MIN_ZOOM_LEVEL = -3.05; // Guard to stop zooming out beyond the chart offset
    public static readonly OFFSET_ZOOM_INNER = 8; // Offset applied when zooming in for inner lines
    public static readonly MIN_SPACING = 5; // Minimum spacing between the lines when zooming out
    public static readonly BASE_SPACING = 30; // Base spacing for polygon lines
    public static readonly BASE_LINE_COUNT = 3; // Default number of lines when zooming is zero
    public static readonly MAX_LINE_COUNT = 20; // Maximum number of lines when zooming out

    // Radius of the outer polygon
    public static readonly OUTER_RADIUS = 100;

    // The width and height of that the SVG requests
    public static readonly SVGBounds = {
        width: SpiderChartFactory.OUTER_RADIUS * 3.5,
        height: SpiderChartFactory.OUTER_RADIUS * 2.8,
    };

    // Offset the center to allow text to be displayed outside the spider chart
    public static readonly SpiderChartCenter: Point2 = {
        x: SpiderChartFactory.SVGBounds.width / 2,
        y: SpiderChartFactory.SVGBounds.height / 2,
    };

    // The offset to make the metric text appear outside the spider chart
    public static readonly TEXT_OFFSETS: Point2[] = [
        { x: 0, y: -28 }, // Top
        { x: 38, y: -8 }, // Right top
        { x: 8, y: 16 }, // Right bottom
        { x: -8, y: 16 }, // Left bottom
        { x: -38, y: -8 }, // Left top
    ];

    /**
     * Creates an SVG spider chart for psychoacoustic metrics.
     * @param psychoAcousticMetricLayout - The layout map of psychoacoustic metrics and their positions on the chart.
     * @param placerId - The ID of the HTML element where the SVG should be appended.
     * @returns The created SVG element.
     */
    public static createSpiderChart(
        psychoAcousticMetricsLayout: PsychoAcousticMetricsLayoutMap,
        placerId: string
    ): SVGElement {
        const svg = SpiderChartFactory.createEmptySVG(
            "100%",
            "40vh",
            `0 0 ${SpiderChartFactory.SVGBounds.width} ${SpiderChartFactory.SVGBounds.height}`
        );

        // Create the outer pentagon element
        const maxRadius = SpiderChartFactory.OUTER_RADIUS;
        const outerPoints = SpiderChartSVGManager.getNGonPoints(5, maxRadius, SpiderChartFactory.SpiderChartCenter);
        const outerPolygon = SpiderChartFactory.createPolygonLine(outerPoints);
        svg.appendChild(outerPolygon);

        // Create the 3 inner pentagon elements, evenly spaced
        for (let i = 0; i < 3; i++) {
            const innerPolygon = SpiderChartFactory.createPolygonLine(
                SpiderChartSVGManager.getNGonPoints(5, maxRadius - 30 * (i + 1), SpiderChartFactory.SpiderChartCenter)
            );
            svg.appendChild(innerPolygon);
        }

        // Create the lines connecting the outer points
        outerPoints.forEach((p) => {
            const line = SpiderChartFactory.createLine(SpiderChartFactory.SpiderChartCenter, p);
            svg.appendChild(line);
        });

        // Create the psychoacoustic text elements
        Object.values(psychoAcousticMetricsLayout).forEach((metric) => {
            const metricText = SpiderChartFactory.createText(metric, placerId);
            svg.appendChild(metricText);
        });

        // Append the SVG to the DOM
        document.getElementById(placerId)?.appendChild(svg);

        return svg;
    }

    /**
     * Create the psychoacoustic metrics for the spider
     * @param points The points of the outer polygon, where the metrics will be displayed
     */
    public static createSpiderChartMetricsLayout(): PsychoAcousticMetricsLayoutMap {
        const maxValuePoints = SpiderChartSVGManager.getNGonPoints(
            5,
            SpiderChartFactory.OUTER_RADIUS,
            SpiderChartFactory.SpiderChartCenter
        );
        const textPoints = maxValuePoints.map((point, i) => Point2.Add(point, SpiderChartFactory.TEXT_OFFSETS[i]));

        return {
            Loudness: new LayoutBuilder()
                .setDisplayName("<val>\nLoudness")
                .setMaxValuePoint(maxValuePoints[0])
                .setTextPoint(textPoints[0])
                .setUnit(PsychoAcousticUnits.Loudness)
                .build(),

            Roughness: new LayoutBuilder()
                .setDisplayName("Roughness\n<val>")
                .setMaxValuePoint(maxValuePoints[1])
                .setTextPoint(textPoints[1])
                .setUnit(PsychoAcousticUnits.Roughness)
                .build(),

            Tonality: new LayoutBuilder()
                .setDisplayName("Tonality\n<val>")
                .setMaxValuePoint(maxValuePoints[2])
                .setTextPoint(textPoints[2])
                .setUnit(PsychoAcousticUnits.Tonality)
                .build(),

            "Fluctuation Strength": new LayoutBuilder()
                .setDisplayName("Fluctuation\nStrength\n<val>")
                .setMaxValuePoint(maxValuePoints[3])
                .setTextPoint(textPoints[3])
                .setUnit(PsychoAcousticUnits["Fluctuation Strength"])
                .build(),

            Sharpness: new LayoutBuilder()
                .setDisplayName("Sharpness\n<val>")
                .setMaxValuePoint(maxValuePoints[4])
                .setTextPoint(textPoints[4])
                .setUnit(PsychoAcousticUnits.Sharpness)
                .build(),
        };
    }

    /**
     * Updates the polygon lines for the spider chart based on the current zoom state.
     * @param svg - The existing SVG element.
     * @param zoomLevel - The current zoom level affecting the distance of the polygon points.
     * @param maxRadius - The maximum radius of the outer polygon.
     */
    public static updatePolygonLines(svg: SVGElement, zoomLevel: number, maxRadius: number): void {
        // Guard: Stop if the zoom level goes below the minimum allowed
        if (zoomLevel <= this.MIN_ZOOM_LEVEL) return;

        // Remove any existing elements with the class "zoomLine"
        const existingLines = Array.from(svg.getElementsByClassName("zoomLine"));
        existingLines.forEach((line) => line.remove());

        // Calculate total number of lines based on zoom level
        let totalLines =
            zoomLevel <= 0
                ? Math.min(this.BASE_LINE_COUNT + Math.abs(zoomLevel), this.MAX_LINE_COUNT) // Add lines when zooming out
                : this.BASE_LINE_COUNT; // Maintain the base number of lines when zooming in

        // Calculate line spacing based on zoom level
        const lineSpacing =
            zoomLevel <= 0
                ? Math.max(this.MIN_SPACING, this.BASE_SPACING - Math.abs(zoomLevel)) // Spacing decreases when zooming out
                : this.BASE_SPACING + zoomLevel * 10; // Spacing increases when zooming in

        // Determine the behavior based on whether zooming in or out
        const isZoomingIn = lineSpacing > this.BASE_SPACING;

        // Create polygon lines based on zoom direction
        for (
            let i = isZoomingIn ? 1 : totalLines - 1;
            isZoomingIn ? i <= totalLines : i >= 0;
            i += isZoomingIn ? 1 : -1
        ) {
            let zoomedRadius = isZoomingIn
                ? i * lineSpacing - this.BASE_SPACING + this.OFFSET_ZOOM_INNER // Zooming in: expanding from inner lines
                : maxRadius - i * lineSpacing; // Zooming out: contracting inward

            // Create polygon lines only if the radius is within maxRadius
            if (zoomedRadius > 0 && zoomedRadius <= maxRadius) {
                const polygonPoints = SpiderChartSVGManager.getNGonPoints(
                    5,
                    zoomedRadius,
                    SpiderChartFactory.SpiderChartCenter
                );

                // Create the polygon element with the adjusted points
                const polygon = SpiderChartFactory.createPolygonLine(polygonPoints);
                polygon.classList.add("zoomLine"); // Add class for easy removal later
                svg.appendChild(polygon);
            }
        }
    }

    /**
     * Creates an empty SVG element.
     * @param width - The width of the SVG.
     * @param height - The height of the SVG.
     * @param viewBox - The viewBox dimensions for scaling.
     * @returns The created empty SVG element.
     */
    public static createEmptySVG(width: string, height: string, viewBox: string): SVGElement {
        return new SVGElementBuilder()
            .createElement("svg")
            .setAttribute("width", width)
            .setAttribute("height", height)
            .setAttribute("viewBox", viewBox)
            .build();
    }

    /**
     * Creates a line element in the SVG.
     * This line connects two points (start and end).
     * @param start - The starting point of the line (x1, y1).
     * @param end - The ending point of the line (x2, y2).
     * @returns The created line SVG element.
     */
    public static createLine(start: Point2, end: Point2): SVGElement {
        return new SVGElementBuilder()
            .createElement("line")
            .setAttribute("x1", start.x.toString())
            .setAttribute("y1", start.y.toString())
            .setAttribute("x2", end.x.toString())
            .setAttribute("y2", end.y.toString())
            .setAttribute("stroke", "#33465b")
            .build();
    }

    /**
     * Creates a polygon element based on a list of points.
     * This is used to create the pentagons in the spider chart.
     * @param points - An array of points representing the vertices of the polygon.
     * @returns The created polygon SVG element.
     */
    public static createPolygonLine(points: Point2[]): SVGElement {
        return new SVGElementBuilder()
            .createElement("polygon")
            .setPoints(points)
            .setAttribute("class", "zoomLine")
            .setFill("none")
            .setStroke("#33465b")
            .build();
    }

    /**
     * Create an SVG polygon element where the points are filled with a semi transparent color bold same color lines
     */
    public static createPolygonFill(points: Point2[], color: string, elementId?: string, hasStroke = true): SVGElement {
        const polygon = new SVGElementBuilder().createElement("polygon").setPoints(points).setFill(color);

        if (hasStroke) polygon.setStroke(color);
        if (elementId) polygon.setIdentifier(elementId);

        return polygon.build();
    }

    /**
     * Creates an SVG text element for a psychoacoustic metric.
     * This places the text label for the metric at the specified point.
     * @param metric - The metric layout properties including text point and display name.
     * @param placerId - The ID of the element where the text will be placed.
     * @returns The created text SVG element.
     */
    public static createText(
        metric: PsychoAcousticMetricLayoutProperties,
        svgPlacerId: string,
        color?: string
    ): SVGElement {
        const position = metric.textPoint;
        const textElement = new SVGElementBuilder()
            .createElement("text")
            .setAttribute("x", position.x.toString())
            .setAttribute("y", position.y.toString())
            .setAttribute("font-size", "small")
            .setAttribute("fill", color || "white")
            .setAttribute("text-anchor", "middle")
            .setAttribute("dominant-baseline", "middle")
            .build();

        // Split the part based on newlines
        const parts = metric.displayName.split("\n");
        parts.forEach((part, index) => {
            const tspan = new SVGElementBuilder().createElement("tspan").setAttribute("x", position.x.toString());

            // Place the text below the previous part
            if (index > 0) {
                tspan.setAttribute("dy", "1.2em");
            }

            // Make the "val" part a unique element so that we can display the value in there
            if (part.includes("<val>")) {
                tspan.setAttribute("id", svgPlacerId + "=" + metric.unit.unit);
                tspan.setAttribute("font-size", "12px");
                part = "";
            }

            let element = tspan.build();
            element.textContent = part;
            textElement.appendChild(element);
        });

        return textElement;
    }
}
